import React from "react";
import CheckBoxList, {
  type CheckBoxListValue,
} from "../../../../../Forms/Controls/CheckBoxList/CheckBoxList";
import {
  dateOptions,
  ageOptions,
  getPlaceholderByField,
  getTreeIconFnByFilterField,
  booleanOptions,
  valueUnitsEnum,
  treeNodeTypesEnum,
} from "../../../../../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import Tree from "../../../../../Components/Tree/Tree";
import type { TreeNode } from "../../../../../Components/Tree/types";
import { resourceTypes } from "../../../../../Findings/FindingsUtils";
import {
  RadioButtonsList,
  TagsSelect,
  TextInput,
} from "@SeemplicityDev/shared";
import DateOrDaysPicker, {
  dateSelectionOptions,
  type DateOrDaysPickerChange,
  type UnitSelectionOption,
} from "../../../../../Components/DateOrDaysPicker/DateOrDaysPicker";
import { widgetKinds, WidgetKind } from "./widgetKinds";

interface DateOrDaysValue {
  value?: string | number;
  unit?: string;
  mode?: string;
}

interface TreeNodeChange {
  id: string;
  type: string;
  key?: string | string[];
  parentId?: string;
}

type FilterValue = string | string[] | DateOrDaysValue | undefined;

// The value emitted from this renderer's onChange, across all widget kinds.
type FilterEmittedValue =
  | string
  | number
  | string[]
  | CheckBoxListValue
  | {
      value: string | number | "" | null | undefined;
      unit: string | undefined;
    }
  | TreeNodeChange[];

interface RadioOption {
  label?: string;
  value: string | number;
  name?: string;
  customOption?: Record<string, unknown>;
}

// Declared as a type alias (not an interface) so it carries an implicit index
// signature and stays assignable to CheckBoxList's CheckBoxListOption.
type FilterValueOption = {
  label: React.ReactNode;
  value: (string | number)[];
  leftImg?: string | null;
  leftIcon?: string | null;
};

export interface ValueComponentRendererProps {
  kind: WidgetKind;
  fieldValue: string;
  value: FilterValue;
  onChange: (value: FilterEmittedValue) => void;
  onBlur: () => void;
  valueOptions: FilterValueOption[];
  loadValues: boolean;
  valueSearchInput: string;
  customValueError: boolean;
  setCustomValueError: (value: boolean) => void;
  textInputType?: string;
  preCheckedNodes?: TreeNode[];
}

const ValueComponentRenderer: React.FC<ValueComponentRendererProps> = ({
  kind,
  fieldValue,
  value,
  onChange,
  onBlur,
  valueOptions,
  loadValues,
  valueSearchInput,
  customValueError,
  setCustomValueError,
  textInputType,
  preCheckedNodes,
}) => {
  const getTextInputOptions = () => (
    <TextInput
      data-testid={"condition-options"}
      onBlur={onBlur}
      wrapperClassName={`filter-values-text-box`}
      className="filter-row-last "
      noMargin
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      }}
      placeholder={getPlaceholderByField(fieldValue) || "+ Add Value"}
      value={typeof value === "string" ? value : undefined}
      applyAutoFocus
      type={textInputType}
    />
  );

  const getTagsList = () => {
    const tagValues = Array.isArray(value) ? value : [];
    return (
      <TagsSelect
        data-testid={"tags-select"}
        onChange={(tags: { name: string }[]) => {
          const tagsList = tags?.map((tag) => tag?.name);
          onChange(tagsList);
        }}
        value={tagValues.map((val) => {
          return { id: val, name: val };
        })}
        placeholder={`Type value...`}
        allowNew={true}
        suggestions={[]}
        noMargin
        minQueryLength={1}
        newTagTextPrefix={`+`}
        autoFocus
        inputType={textInputType}
      />
    );
  };

  const getDateRadioButtonsList = (menuOptions: RadioOption[]) => {
    const isOldFormat = typeof value === "string"; // we check for the old format at the moment for backward compatibility
    if (!value) {
      onChange(menuOptions?.[0]?.value);
    }
    const dateValue =
      typeof value === "object" && !Array.isArray(value) ? value : undefined;
    return (
      <RadioButtonsList
        data-testid={"date-radio-buttons-list"}
        noMargin
        value={isOldFormat ? value : dateValue?.value?.toString()}
        onChange={(val: string) => {
          const valAsInt = parseInt(val);
          const valueForSetValue = {
            value: isNaN(valAsInt) ? "" : valAsInt,
            unit: valueUnitsEnum.DAYS,
          };
          onChange(valueForSetValue);
          if (customValueError) {
            setCustomValueError(false);
          }
        }}
        options={menuOptions}
        direction={"column"}
      />
    );
  };

  const getRadioButtonsList = (menuOptions: RadioOption[]) => {
    return (
      <RadioButtonsList
        data-testid={"radio-buttons-list"}
        noMargin
        value={typeof value === "string" ? value : ""}
        onChange={(val: string) => {
          onChange(val);
          if (customValueError) {
            setCustomValueError(false);
          }
        }}
        options={menuOptions}
        direction={"column"}
      />
    );
  };

  const getCheckboxList = () => {
    return (
      <CheckBoxList
        data-testid={"filter-dropdown-item"}
        className={"filter-dropdown-item"}
        value={Array.isArray(value) ? value : undefined}
        onChange={(val: CheckBoxListValue) => onChange(val)}
        options={valueOptions}
        direction={"column"}
        loading={loadValues}
        inDropdown
        withBg
        textToHighlight={valueSearchInput}
      />
    );
  };

  const getDateOptions = () => {
    const isOldFormat = typeof value === "string"; // we check for the old format at the moment for backward compatibility
    const dateValue =
      typeof value === "object" && !Array.isArray(value) ? value : undefined;
    const currentVal = isOldFormat ? value : dateValue?.value;
    const currentSelectionType = isOldFormat
      ? dateSelectionOptions.DATE
      : dateValue?.unit
        ? dateValue?.unit === valueUnitsEnum.ABSOLUTE
          ? dateSelectionOptions.DATE
          : dateSelectionOptions.DAYS
        : null;
    const currentUnit =
      isOldFormat || dateValue?.unit === valueUnitsEnum.ABSOLUTE
        ? null
        : dateValue?.unit;
    return (
      <DateOrDaysPicker
        data-testid={"date-picker"}
        value={currentVal}
        savedSelectionType={currentSelectionType}
        savedUnit={currentUnit as UnitSelectionOption | null | undefined}
        onChange={(value: DateOrDaysPickerChange) => {
          onChange({
            value:
              value.mode === dateSelectionOptions.DATE
                ? value?.value
                : parseInt(value?.value as string),
            unit:
              value.mode === dateSelectionOptions.DATE
                ? valueUnitsEnum.ABSOLUTE
                : value?.unit,
          });
          setCustomValueError(!value?.value);
        }}
        onBlur={onBlur}
        maxYears={50}
        setInvalid={setCustomValueError}
      />
    );
  };

  const getCheckedNodes = (nodes: TreeNodeChange[]) => {
    const data: TreeNodeChange[] = [];

    const categories =
      nodes
        ?.filter((item) => item?.type === treeNodeTypesEnum.CATEGORY)
        ?.map((item) => item.id) || [];

    nodes?.forEach((node) => {
      if (node.type === treeNodeTypesEnum.CATEGORY) {
        data.push({ id: node.id, type: node.type, key: node.key });
      } else if (node.type === treeNodeTypesEnum.KEY) {
        if (!categories.includes(node.parentId ?? "")) {
          data.push({
            id: node.id,
            type: node.type,
            key: Array.isArray(node.key)
              ? node.key.map((item) => item.toString())
              : node.key,
          });
        }
      }
    });

    return data;
  };

  const getTree = () => (
    <Tree
      data={valueOptions as unknown as TreeNode[]}
      isSelectableNode={(node: TreeNode) => node.type !== resourceTypes.ROOT}
      isHiddenRoot
      tristate
      onChange={(nodes: TreeNode[]) => {
        // Tree emits fully-populated nodes at runtime; narrow to the
        // TreeNodeChange view getCheckedNodes consumes.
        const data = getCheckedNodes(nodes as TreeNodeChange[]);
        onChange(data);
      }}
      nodeOperations={{
        onEdit: () => {},
        onSelect: () => {},
        onWarning: () => false,
      }}
      data-testid={"actual-status-tree"}
      withCheckbox
      className={`dropdown-tree`}
      borderlessNodes
      preCheckedNodes={preCheckedNodes}
      getNodeIconByType={getTreeIconFnByFilterField(fieldValue)}
    />
  );

  switch (kind) {
    case widgetKinds.BOOLEAN_RADIOS:
      return getRadioButtonsList(booleanOptions);
    case widgetKinds.TREE:
      return getTree();
    case widgetKinds.CHECKBOX:
      return getCheckboxList();
    case widgetKinds.TAGS:
      return getTagsList();
    case widgetKinds.AGE_RADIOS:
      return getRadioButtonsList(
        ageOptions(fieldValue, () => {
          setCustomValueError(true);
        })
      );
    case widgetKinds.DATE_PICKER:
      return getDateOptions();
    case widgetKinds.DATE_RADIOS:
      return getDateRadioButtonsList(
        dateOptions(fieldValue, () => {
          setCustomValueError(true);
        })
      );
    case widgetKinds.TEXT_INPUT:
      return getTextInputOptions();
    default: {
      kind satisfies never;
      return getTextInputOptions();
    }
  }
};

export default ValueComponentRenderer;
